import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { motion } from "framer-motion";

const EKGPulse = () => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(800);

  // Responsive width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Path generator uses dynamic width
  const createEkgPath = useCallback(() => {
    let path = "M0,40";
    let x = 0;
    const random = (min, max) => Math.random() * (max - min) + min;
    const createPulse = (startX, type) => {
      switch (type) {
        case "double":
          return {
            path: ` L${startX + 5},${random(20, 30)} L${startX + 10},40 L${startX + 15},${random(5, 15)} L${startX + 20},${random(55, 75)} L${startX + 25},40`,
            width: 25,
          };
        case "small":
          return {
            path: ` L${startX + 5},${random(25, 35)} L${startX + 10},${random(45, 55)} L${startX + 15},40`,
            width: 15,
          };
        case "normal":
        default:
          return {
            path: ` L${startX + 5},${random(5, 15)} L${startX + 10},${random(55, 75)} L${startX + 15},40`,
            width: 15,
          };
      }
    };

    while (x < width) {
      if (x < 325) {
        path += ` L${x + 50},40`;
        x += 50;
      } else {
        const baselineLength = random(15, 45);
        path += ` L${x + baselineLength},40`;
        x += baselineLength;
        const pulseType =
          Math.random() < 0.3
            ? "double"
            : Math.random() < 0.5
              ? "small"
              : "normal";
        const pulse = createPulse(x, pulseType);
        if (x + pulse.width > width) break; // Don't overflow
        path += pulse.path;
        x += pulse.width;
        const postPulseLength = random(10, 30);
        if (x + postPulseLength > width) break;
        path += ` L${x + postPulseLength},40`;
        x += postPulseLength;
      }
    }
    // End at the baseline at the right edge
    path += ` L${width},40`;
    return path;
  }, [width]);

  const ekgPath = useMemo(() => createEkgPath(), [createEkgPath]);

  return (
    <div ref={containerRef} className="relative h-20 mt-2 w-full">
      <svg
        width={width}
        height="80"
        viewBox={`0 0 ${width} 80`}
        className="text-mblue-700"
      >
        <motion.path
          d={ekgPath}
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2.5, ease: "easeInOut" }}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

export default React.memo(EKGPulse);
